import React, { useState, useEffect, useRef } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

/**
 * LanguageSelector Component
 * A dropdown for selecting the preferred language using the paid Google Translation API.
 * This is NOT a floating widget - it's an inline component that can be placed anywhere.
 */
const LanguageSelector = ({ 
  variant = 'default', // 'default', 'minimal', 'header', 'light'
  className = '',
  showLabel = true,
  onLanguageChange 
}) => {
  const [languages, setLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const dropdownRef = useRef(null);
  
  // Get filter background color from theme
  const { adminTheme } = useTheme();
  const filterBgColor = adminTheme?.filters?.backgroundColor || '#D4F6FF';

  useEffect(() => {
    fetchLanguages();
    // Load saved preference
    const saved = localStorage.getItem('preferred_language');
    if (saved) {
      setSelectedLanguage(saved);
    }
  }, []);

  useEffect(() => {
    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchLanguages = async () => {
    try {
      // First check if translation is enabled
      const statusRes = await api.get('/integrations/google-translate/status');
      setEnabled(statusRes.data.enabled);
      
      if (statusRes.data.enabled) {
        // Fetch supported languages
        const res = await api.get('/integrations/supported-languages');
        setLanguages(res.data.languages || []);
      }
    } catch (error) {
      console.log('Translation not available');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLanguage = (code) => {
    setSelectedLanguage(code);
    localStorage.setItem('preferred_language', code);
    setIsOpen(false);
    
    if (onLanguageChange) {
      onLanguageChange(code);
    }
    
    // Trigger custom event for other components to react
    window.dispatchEvent(new CustomEvent('languageChange', { detail: { language: code } }));
  };

  const getLanguageName = (code) => {
    const lang = languages.find(l => l.code === code);
    return lang?.name || code.toUpperCase();
  };

  // Don't render if translation is not enabled or no languages available
  if (!enabled || loading || languages.length === 0) {
    return null;
  }

  // Variant styles - use theme filter color
  const variants = {
    default: {
      button: 'flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm text-gray-700',
      buttonBg: filterBgColor,
      dropdown: 'absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50',
      item: 'flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer',
    },
    minimal: {
      button: 'flex items-center gap-1.5 px-2 py-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors text-sm',
      buttonBg: filterBgColor,
      dropdown: 'absolute top-full right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50',
      item: 'flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer',
    },
    header: {
      button: 'flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 rounded-lg transition-colors text-sm',
      buttonBg: filterBgColor,
      dropdown: 'absolute top-full right-0 mt-1 w-48 bg-white border border-gray-200 rounded-xl shadow-xl py-2 z-50',
      item: 'flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer',
    },
    light: {
      button: 'flex items-center gap-2 px-3 py-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors text-sm',
      buttonBg: 'transparent',
      dropdown: 'absolute top-full left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50',
      item: 'flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 cursor-pointer text-gray-700',
    },
    public: {
      button: 'flex items-center gap-2 px-4 py-2.5 backdrop-blur-sm border border-white/20 rounded-xl hover:bg-white/20 transition-colors text-sm text-gray-700',
      buttonBg: filterBgColor,
      dropdown: 'absolute top-full left-0 mt-2 w-52 bg-white border border-gray-200 rounded-xl shadow-xl py-2 z-50',
      item: 'flex items-center justify-between px-4 py-2.5 text-sm hover:bg-gray-50 cursor-pointer',
    }
  };

  const style = variants[variant] || variants.default;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={style.button}
        style={{ backgroundColor: style.buttonBg }}
        data-testid="language-selector-btn"
      >
        {showLabel && (
          <span className="notranslate" translate="no">{getLanguageName(selectedLanguage)}</span>
        )}
        <Globe className="w-4 h-4" />
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className={style.dropdown}>
          <div className="max-h-64 overflow-y-auto">
            {languages.map((lang) => (
              <div
                key={lang.code}
                onClick={() => handleSelectLanguage(lang.code)}
                className={`${style.item} ${selectedLanguage === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}`}
                data-testid={`language-option-${lang.code}`}
              >
                <span className="notranslate" translate="no">{lang.name}</span>
                {selectedLanguage === lang.code && (
                  <Check className="w-4 h-4 text-blue-600" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
